import React from "react";
import { PopUpConstants, shared } from "../../src/taskpane/config/constants";
import { IAuthTokenResultDto } from "../../src/taskpane/interfaces/IAuthTokenResultDto";
import authModule, { DialogMessageResponse, DialogMessageStatus, TokenResource } from "../../utilities/AuthModule";
import generalModule from "../../utilities/GeneralModule";
import { logger } from "../../utilities/logger";
import { getTessAccounts, ITessAccountSite, ITessAccountsResult, toTessAccountSite } from "../../src/taskpane/services/TessService";
import { ModeEnum } from "../../src/taskpane/components/enums/modeEnum";
import { OutlookUtils } from "../../utilities/OutlookUtils";

export interface ITessAuthHook {
    isTessTokenAvailable: boolean;
    isTessRequestInProgress: boolean;
    allowLoadAllAccounts: boolean;
    isAllAccountsLoaded: boolean;
    tessAccountSites: ITessAccountSite[];
    requestTessToken: () => Promise<void>;
    loadTessAccounts: (setProgreggStatus?: boolean) => Promise<void>;
    toggleAllTessAccounts: (loadAllAccounts: boolean) => Promise<void>;
}

export function useTessPopUpAuth(isTessEnabled: boolean, mode?: ModeEnum): ITessAuthHook {
    const [isTessTokenAvailable, setIsTessTokenAvailable] = React.useState<boolean>(false);
    const [isTessRequestInProgress, setIsTessRequestInProgress] = React.useState<boolean>(false);
    const [allowLoadAllAccounts, setAllowLoadAllAccounts] = React.useState<boolean>(false);
    const [isAllAccountsLoaded, setIsAllAccountsLoaded] = React.useState<boolean>(false);
    const [tessAccountSites, setTessAccountSites] = React.useState<ITessAccountSite[]>([]);

    const authDialogRef = React.useRef<any>();

    const requestTessToken = async () => {
        if (generalModule.isInConentMangerMode()) {
            let authState: IAuthTokenResultDto = authModule.getPersistedAuthState();

            let authUrl: URL = new URL(`${process.env.baseUri}/auth.html`);
            authUrl.searchParams.append(shared.params.app, "content_manager");
            authUrl.searchParams.append(shared.params.resource, TokenResource.Tess.toString());
            authUrl.searchParams.append(shared.params.username, authState.account?.username);
            authUrl.searchParams.append(shared.params.tenantId, authState.account?.tenantId);
            authUrl.searchParams.append(shared.params.accountId, authState.account?.uniqueId);
            /**
             * adding winLeft and winTop to account for dual monitor
             * using screenLeft and screenTop for IE8 and earlier
             */
            const winLeft = window.screenLeft ? window.screenLeft : window.screenX;
            const winTop = window.screenTop ? window.screenTop : window.screenY;
            /**
             * window.innerWidth displays browser window"s height and width excluding toolbars
             * using document.documentElement.clientWidth for IE8 and earlier
             */
            const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
            const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
            const left = Math.max(0, ((width / 2) - (PopUpConstants.POPUP_WIDTH / 2)) + winLeft);
            const top = Math.max(0, ((height / 2) - (PopUpConstants.POPUP_HEIGHT / 2)) + winTop);

            authDialogRef.current = window.open(authUrl.href, `${PopUpConstants.POPUP_NAME_PREFIX}.${TokenResource.Tess}`, `width=${PopUpConstants.POPUP_WIDTH}, height=${PopUpConstants.POPUP_HEIGHT}, top=${top}, left=${left}, scrollbars=yes`);
        }
        else {
            await authModule.initiateDialogAuthFlow(loginCallback, TokenResource.Tess);
        }
    }

    const processAuthDialogResponse = (event) => {
        if (event.origin != process.env.baseUri)
            return;

        try {
            if (event.data) {
                const messageFromDialog: DialogMessageResponse = event.data;
                if (messageFromDialog?.status === DialogMessageStatus.Success) {
                    let authState: IAuthTokenResultDto = authModule.getPersistedAuthState();
                    if (authState) {
                        (async () => {
                            const isTessAuthenticated: boolean = messageFromDialog.result.isTessAuthenticated;
                            authState.isTessAuthenticated = isTessAuthenticated;
                            authModule.persistAuthState(authState);
                            if (isTessAuthenticated) {
                                await loadTessAccounts(true);
                            }
                            setIsTessTokenAvailable(isTessAuthenticated);
                        })();
                    }
                }
            }
        } catch (error) {
            logger(error, "useTessPopUpAuth.tsx (processAuthDialogResponse)", event.data);
        }

        authDialogRef?.current?.close();
    }

    const loginCallback = async (authResult: IAuthTokenResultDto) => {
        if (authResult) {
            if (authResult.isTessAuthenticated) {
                await loadTessAccounts(true);
            }
            setIsTessTokenAvailable(authResult.isTessAuthenticated);
        }
    };

    const loadTessAccounts = async (setProgressStatus?: boolean) => {
        if (setProgressStatus) setIsTessRequestInProgress(true);

        let accountEmailAddresses: string[];
        if (mode === ModeEnum.mailRead) {
            const senderEmailAddress: string = Office?.context?.mailbox?.item?.from?.emailAddress;

            accountEmailAddresses = senderEmailAddress
                ? [senderEmailAddress]
                : [];

            if (Array.isArray(Office?.context?.mailbox?.item?.to)) {
                const profileEmailAddress: string = authModule.getPersistedAuthState()?.account?.username?.trim()?.toLowerCase();

                const recipients: Office.EmailAddressDetails[] = Office?.context?.mailbox?.item?.to
                    .filter(x => x.emailAddress && x.emailAddress.trim().toLowerCase() !== profileEmailAddress);

                if (recipients.length > 0) {
                    accountEmailAddresses.push(...recipients.map(x => x.emailAddress));
                }
            }
        }
        else if (mode === ModeEnum.mailCompose) {
            const recipients: Office.EmailAddressDetails[] = await OutlookUtils.getItemTo();
            if (Array.isArray(recipients) && recipients.length > 0) {
                accountEmailAddresses = recipients
                    .map(x => x.emailAddress?.trim())
                    .filter(x => x);
            }
        }

        const accountsResult: ITessAccountsResult = await getTessAccounts(accountEmailAddresses?.filter((emailAddress, i, arr) => i === arr.findIndex(x => x === emailAddress)));
        const accountSites: ITessAccountSite[] = accountsResult?.accounts?.map(account => toTessAccountSite(account));

        setIsAllAccountsLoaded(false);
        setAllowLoadAllAccounts(accountsResult?.allowLoadAllAccounts);
        setTessAccountSites(accountSites ?? []);
        if (setProgressStatus) setIsTessRequestInProgress(false);
    }

    const toggleAllTessAccounts = React.useCallback(async (loadAllAccounts: boolean) => {
        if (loadAllAccounts) {
            const accountsResult: ITessAccountsResult = await getTessAccounts();
            const accountSites: ITessAccountSite[] = accountsResult?.accounts?.map(account => toTessAccountSite(account, true));

            setIsAllAccountsLoaded(true);
            setTessAccountSites(prev => prev
                .concat(accountSites ?? [])
                .filter((accountSite, i, arr) => i === arr.findIndex(x => x.accountId === accountSite.accountId))
            );
        }
        else {
            setIsAllAccountsLoaded(false);
            setTessAccountSites(prev => prev.filter(x => !x.allowToggle));
        }
    }, [isAllAccountsLoaded, tessAccountSites]);

    React.useEffect(() => {
        if (isTessEnabled) {
            window.addEventListener("message", processAuthDialogResponse);
            (async () => {
                setIsTessRequestInProgress(true);
                const isTessAuthenticated: boolean = await authModule.getIsTessAuthenticated(true);
                if (isTessAuthenticated) {
                    await loadTessAccounts();
                }
                setIsTessTokenAvailable(isTessAuthenticated);
                setIsTessRequestInProgress(false);
            })();
        }
        return () => {
            window.removeEventListener("message", processAuthDialogResponse);
        };
    }, [isTessEnabled]);

    return { isTessTokenAvailable, isTessRequestInProgress, allowLoadAllAccounts, isAllAccountsLoaded, tessAccountSites, requestTessToken, loadTessAccounts, toggleAllTessAccounts }
}